const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// TEMPORARY LOCAL QA: bypass the native Contact POST and render the success flow in place.
// Set to false (or remove this branch) before production validation.
const MOCK_AVAILABILITY_REQUEST_SUCCESS = false;

class KodaRequestAvailability extends HTMLElement {
  #dialog;
  #form;
  #section;
  #currentVariant;
  #opener;
  #drafts = new Map();
  #submittedVariantIds = new Set();
  #abortController = new AbortController();
  #closingTimer;
  #successTimer;

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    this.#dialog = this.querySelector('[data-request-dialog]');
    this.#form = this.querySelector('form');
    this.#section = this.closest('.shopify-section');
    this.#currentVariant = this.#readJson('[data-request-current-variant]');

    const { signal } = this.#abortController;
    this.querySelector('[data-request-open]')?.addEventListener('click', this.#open, { signal });
    this.querySelectorAll('[data-request-close]').forEach((button) => {
      button.addEventListener('click', this.#close, { signal });
    });
    this.querySelector('[data-request-back]')?.addEventListener('click', this.#backToProduct, { signal });
    this.#dialog?.addEventListener('cancel', this.#handleCancel, { signal });
    this.#dialog?.addEventListener('click', this.#handleBackdropClick, { signal });
    this.#dialog?.addEventListener('keydown', this.#trapFocus, { signal });
    this.#form?.addEventListener('submit', this.#handleSubmit, { capture: true, signal });
    this.querySelector('[data-request-submit]')?.addEventListener('click', this.#handleMockSubmitClick, {
      capture: true,
      signal,
    });
    this.#form?.addEventListener('invalid', this.#handleInvalid, { capture: true, signal });
    this.querySelector('[data-request-email]')?.addEventListener('input', this.#clearEmailError, { signal });
    this.#section?.addEventListener('variant:selected', this.#handleVariantSelected, { signal });
    this.#section?.addEventListener('variant:update', this.#handleVariantUpdate, { signal });
    window.addEventListener('pageshow', this.#resetSubmitting, { signal });

    this.#applyVariant(this.#currentVariant, { restoreDraft: false });
    this.#handleServerState();
  }

  disconnectedCallback() {
    this.#togglePurchaseForms(false);
    this.#abortController.abort();
    window.clearTimeout(this.#closingTimer);
    window.clearTimeout(this.#successTimer);
  }

  #readJson(selector) {
    const script = this.querySelector(selector);
    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (_error) {
      return null;
    }
  }

  #variantById(id) {
    if (!id) return null;
    const variants = this.#readJson('[data-request-variants]');
    if (!Array.isArray(variants)) return null;
    return variants.find((variant) => String(variant.id) === String(id)) || null;
  }

  #displayVariantTitle(variant) {
    if (!variant?.title || variant.title === 'Default Title') return 'Default configuration';
    return variant.title;
  }

  #defaultSubject(variant) {
    return `Availability request — ${this.dataset.displayProductTitle} / ${this.#displayVariantTitle(variant)}`;
  }

  #defaultMessage(variant) {
    return [
      'Hi Koda Machined,',
      '',
      `I'd like to request availability for the ${this.dataset.displayProductTitle} in ${this.#displayVariantTitle(variant)}.`,
      '',
      'Please let me know if this configuration can be sourced or restocked.',
      '',
      'Thanks.',
    ].join('\n');
  }

  #draftKey(variant = this.#currentVariant) {
    return variant?.id ? String(variant.id) : '';
  }

  #captureDraft() {
    const key = this.#draftKey();
    if (!key || !this.#form) return null;

    const draft = {
      email: this.querySelector('[data-request-email]')?.value || '',
      subject: this.querySelector('[data-request-subject]')?.value || '',
      message: this.querySelector('[data-request-message]')?.value || '',
    };
    this.#drafts.set(key, draft);
    return draft;
  }

  #restoreDraft(draft) {
    if (!draft) return;
    const email = this.querySelector('[data-request-email]');
    const subject = this.querySelector('[data-request-subject]');
    const message = this.querySelector('[data-request-message]');
    if (email && typeof draft.email === 'string') email.value = draft.email;
    if (subject && typeof draft.subject === 'string') subject.value = draft.subject;
    if (message && typeof draft.message === 'string') message.value = draft.message;
  }

  #setField(selector, value) {
    this.querySelectorAll(selector).forEach((element) => {
      if ('value' in element) element.value = value ?? '';
      else element.textContent = value ?? '';
    });
  }

  #updateContext(variant) {
    if (!variant) return;

    const displayVariant = this.#displayVariantTitle(variant);
    const sku = variant.sku || '';
    const variantUrl = new URL(this.dataset.productPath, window.location.origin);
    variantUrl.searchParams.set('variant', String(variant.id));

    this.#setField('[data-request-summary-product]', this.dataset.displayProductTitle);
    this.#setField('[data-request-summary-variant]', displayVariant);

    this.#setField('[data-request-meta="product"]', this.dataset.displayProductTitle);
    this.#setField('[data-request-meta="brand"]', this.dataset.brand || '');
    this.#setField('[data-request-meta="variant"]', variant.title || '');
    this.#setField('[data-request-meta="sku"]', sku);
    this.#setField('[data-request-meta="product_url"]', variantUrl.href);
    this.#setField('[data-request-meta="variant_id"]', String(variant.id));
    this.#setField('[data-request-meta="product_id"]', this.dataset.productId);

    const returnTo = this.#form?.querySelector('input[name="return_to"]');
    if (returnTo) {
      variantUrl.searchParams.set('availability_request', 'success');
      returnTo.value = `${variantUrl.pathname}${variantUrl.search}#${this.dataset.blockAnchor}`;
    }
  }

  #applyVariant(variant, { restoreDraft = true } = {}) {
    this.#currentVariant = variant?.id ? variant : null;
    const hasVariant = Boolean(this.#currentVariant);
    const available = hasVariant && this.#currentVariant.available !== false;

    this.dataset.hasVariant = String(hasVariant);
    this.dataset.available = String(available);
    this.#togglePurchaseForms(hasVariant && !available);
    if (!hasVariant) return;

    this.#updateContext(this.#currentVariant);
    this.#renderSubmittedState();

    const draft = restoreDraft ? this.#drafts.get(this.#draftKey()) : null;
    if (draft) {
      this.#restoreDraft(draft);
      return;
    }

    const subject = this.querySelector('[data-request-subject]');
    const message = this.querySelector('[data-request-message]');
    if (subject) subject.value = this.#defaultSubject(this.#currentVariant);
    if (message) message.value = this.#defaultMessage(this.#currentVariant);
  }

  #isCurrentVariantSubmitted() {
    return Boolean(this.#currentVariant?.id && this.#submittedVariantIds.has(String(this.#currentVariant.id)));
  }

  #renderSubmittedState() {
    const submitted = this.#isCurrentVariantSubmitted();
    this.dataset.submitted = String(submitted);

    const trigger = this.querySelector('[data-request-open]');
    const defaultContent = this.querySelector('[data-request-trigger-default]');
    const completeContent = this.querySelector('[data-request-trigger-complete]');
    const defaultHelper = this.querySelector('[data-request-helper-default]');
    const completeHelper = this.querySelector('[data-request-helper-complete]');

    if (defaultContent) defaultContent.hidden = submitted;
    if (completeContent) completeContent.hidden = !submitted;
    if (defaultHelper) defaultHelper.hidden = submitted;
    if (completeHelper) completeHelper.hidden = !submitted;

    if (trigger) {
      trigger.setAttribute('aria-disabled', String(submitted));
      trigger.setAttribute(
        'aria-label',
        submitted ? 'You’ll be notified for this configuration' : 'Notify me when available'
      );
    }

    if (!submitted) this.#showFormContent();
  }

  #showFormContent() {
    const success = this.querySelector('[data-request-success]');
    const formState = this.querySelector('[data-request-form-state]');
    const description = this.querySelector('[data-request-description]');
    if (success) success.hidden = true;
    if (formState) formState.hidden = false;
    if (description) {
      description.hidden = false;
      this.#dialog?.setAttribute('aria-describedby', description.id);
    }
    this.dataset.serverState = 'form';
  }

  #showSuccessContent() {
    const success = this.querySelector('[data-request-success]');
    const formState = this.querySelector('[data-request-form-state]');
    const description = this.querySelector('[data-request-description]');
    if (success) success.hidden = false;
    if (formState) formState.hidden = true;
    if (description) description.hidden = true;
    this.#dialog?.removeAttribute('aria-describedby');
    this.dataset.serverState = 'success';
  }

  #togglePurchaseForms(requestActive) {
    const details = this.closest('.product-details');
    const buyButtons = details?.querySelector('.buy-buttons-block');
    if (buyButtons) {
      if (requestActive) {
        if (!buyButtons.hidden) buyButtons.dataset.kodaRequestSuppressed = 'true';
        buyButtons.hidden = true;
        buyButtons.setAttribute('inert', '');
      } else if (buyButtons.dataset.kodaRequestSuppressed === 'true') {
        buyButtons.hidden = false;
        buyButtons.removeAttribute('inert');
        delete buyButtons.dataset.kodaRequestSuppressed;
      }
    }

    const stickyCart = this.#section?.querySelector('sticky-add-to-cart');
    if (stickyCart) {
      if (requestActive) {
        if (!stickyCart.hidden) stickyCart.dataset.kodaRequestSuppressed = 'true';
        stickyCart.hidden = true;
      } else if (stickyCart.dataset.kodaRequestSuppressed === 'true') {
        stickyCart.hidden = false;
        delete stickyCart.dataset.kodaRequestSuppressed;
      }
    }
  }

  #handleVariantSelected = (event) => {
    if (!this.contains(event.target) && !this.#section?.contains(event.target)) return;
    this.#captureDraft();
    const instantVariant = event.detail?.resource?.variant;
    if (instantVariant?.id) this.#applyVariant(instantVariant, { restoreDraft: true });
  };

  #handleVariantUpdate = (event) => {
    const eventProductId = event.detail?.data?.productId;
    if (eventProductId && String(eventProductId) !== String(this.dataset.productId)) return;

    const variant = event.detail?.resource;
    if (variant?.id) this.#applyVariant(variant, { restoreDraft: true });
    else this.#applyVariant(null);
  };

  #open = (event) => {
    if (!this.#dialog || this.#dialog.open || !this.#currentVariant) return;
    const isSuccessRecovery = event == null && this.dataset.successPending === 'true';
    if (this.#isCurrentVariantSubmitted() && !isSuccessRecovery) return;

    window.clearTimeout(this.#successTimer);
    this.#opener = event?.currentTarget instanceof HTMLElement
      ? event.currentTarget
      : this.querySelector('[data-request-open]');
    this.#updateContext(this.#currentVariant);
    this.#dialog.showModal();

    requestAnimationFrame(() => {
      const target = isSuccessRecovery
        ? this.querySelector('[data-request-success]')
        : this.querySelector('[data-request-email]');
      target?.focus({ preventScroll: true });
      if (isSuccessRecovery) {
        delete this.dataset.successPending;
        this.#successTimer = window.setTimeout(this.#close, 10000);
      }
    });
  };

  #close = () => {
    window.clearTimeout(this.#successTimer);
    this.#successTimer = undefined;
    if (!this.#dialog?.open || this.#dialog.dataset.closing === 'true') return;
    if (this.dataset.serverState !== 'success') this.#captureDraft();
    this.#dialog.dataset.closing = 'true';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#closingTimer = window.setTimeout(() => {
      this.#dialog.close();
      delete this.#dialog.dataset.closing;
      if (this.#opener instanceof HTMLElement && this.#opener.isConnected) this.#opener.focus();
    }, reducedMotion ? 0 : 145);
  };

  #backToProduct = () => {
    this.#close();
  };

  #handleCancel = (event) => {
    event.preventDefault();
    this.#close();
  };

  #handleBackdropClick = (event) => {
    if (event.target === this.#dialog) this.#close();
  };

  #trapFocus = (event) => {
    if (event.key !== 'Tab') return;
    const focusable = [...this.#dialog.querySelectorAll(FOCUSABLE_SELECTOR)].filter((element) => {
      return element instanceof HTMLElement && !element.hidden && element.getClientRects().length > 0;
    });
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  #showGlobalError(message) {
    const error = this.querySelector('[data-request-global-error]');
    if (!error) return;
    const strong = error.querySelector('strong');
    if (strong) strong.textContent = message;
    error.classList.remove('is-hidden');
    error.focus({ preventScroll: false });
  }

  #handleInvalid = (event) => {
    if (event.target !== this.querySelector('[data-request-email]')) return;
    const input = event.target;
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', this.querySelector('[data-request-email-error]')?.id || '');
    this.querySelector('[data-request-email-error]')?.classList.remove('is-hidden');
  };

  #clearEmailError = () => {
    const input = this.querySelector('[data-request-email]');
    const error = this.querySelector('[data-request-email-error]');
    if (!input?.validity.valid) return;
    input.removeAttribute('aria-invalid');
    input.removeAttribute('aria-describedby');
    error?.classList.add('is-hidden');
  };

  #handleSubmit = (event) => {
    if (MOCK_AVAILABILITY_REQUEST_SUCCESS) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.#form?.reportValidity()) this.#completeMockSubmission();
      return;
    }

    if (this.#isCurrentVariantSubmitted()) {
      event.preventDefault();
      return;
    }

    if (!navigator.onLine) {
      event.preventDefault();
      this.#showGlobalError("We couldn't send your request. Check your connection and try again.");
      return;
    }

    if (!this.#currentVariant) {
      event.preventDefault();
      this.#showGlobalError("We couldn't send your request. Select a valid configuration and try again.");
      return;
    }

    this.#updateContext(this.#currentVariant);
    this.#captureDraft();

    const submit = this.querySelector('[data-request-submit]');
    if (submit) submit.disabled = true;
    const label = this.querySelector('[data-request-submit-label]');
    if (label) label.textContent = 'Sending…';
    this.dataset.submitting = 'true';
  };

  #handleMockSubmitClick = (event) => {
    if (!MOCK_AVAILABILITY_REQUEST_SUCCESS) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.#form?.reportValidity()) this.#completeMockSubmission();
  };

  #completeMockSubmission() {
    if (!this.#currentVariant || this.#isCurrentVariantSubmitted()) return;

    this.#updateContext(this.#currentVariant);
    this.#captureDraft();
    this.#setField('[data-request-success-email]', this.querySelector('[data-request-email]')?.value || '');
    this.#submittedVariantIds.add(String(this.#currentVariant.id));
    this.#renderSubmittedState();
    this.#showSuccessContent();
    this.#resetSubmitting();

    requestAnimationFrame(() => {
      this.querySelector('[data-request-success]')?.focus({ preventScroll: true });
      window.clearTimeout(this.#successTimer);
      this.#successTimer = window.setTimeout(this.#close, 10000);
    });
  }

  #resetSubmitting = () => {
    const submit = this.querySelector('[data-request-submit]');
    if (submit) submit.disabled = false;
    const label = this.querySelector('[data-request-submit-label]');
    if (label) label.textContent = 'Notify me';
    delete this.dataset.submitting;
  };

  #handleServerState() {
    const serverState = this.querySelector('[data-request-server-state]')?.dataset.requestServerState || 'form';
    const url = new URL(window.location.href);
    const successMarker = url.searchParams.get('availability_request') === 'success';
    const returnedVariantId = url.searchParams.get('variant');

    // Shopify only follows this dedicated return_to URL after a successful native Contact POST.
    // After CAPTCHA, posted_successfully? and contact_posted aren't guaranteed to survive the
    // redirect to the PDP, so the scoped return marker is the reliable recovery signal.
    if (successMarker && returnedVariantId) {
      const submittedVariant = this.#variantById(returnedVariantId) || this.#currentVariant;
      if (!submittedVariant || String(submittedVariant.id) !== String(returnedVariantId)) return;

      this.#submittedVariantIds.add(String(returnedVariantId));
      this.#applyVariant(submittedVariant, { restoreDraft: false });
      this.#showSuccessContent();
      this.#cleanReturnUrl({ removeVariant: true });
      this.dataset.successPending = 'true';
      requestAnimationFrame(() => this.#open());
      return;
    }

    if (successMarker) this.#cleanReturnUrl({ removeVariant: false });

    if (serverState === 'error') {
      this.#showFormContent();
      requestAnimationFrame(() => this.#open());
    }
  }

  #cleanReturnUrl({ removeVariant }) {
    const url = new URL(window.location.href);
    url.searchParams.delete('availability_request');
    url.searchParams.delete('contact_posted');
    if (removeVariant) url.searchParams.delete('variant');
    if (url.hash === `#${this.dataset.blockAnchor}`) url.hash = '';
    history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }
}

if (!customElements.get('koda-request-availability')) {
  customElements.define('koda-request-availability', KodaRequestAvailability);
}
